import { useEffect, useRef, useState } from 'react';

import type { KassenApi, Zahlung } from '../api';
import { formatiereEuro } from '../warenkorb';
import { Rueckfrage } from './Rueckfrage';
import { useFokusfalleAmFenster } from './zahlung-v2/fokus';
import { KartenDialogV2 } from './zahlung-v2/KartenDialogV2';
import { zahlungsdialogV2Aktiv } from './zahlung-v2/schalter';

/**
 * Kartenzahlung läuft am Terminal; die Kasse zeigt den Stand und kann
 * abbrechen. Der Dialog endet erst, wenn die API einen Endzustand meldet –
 * nur „bezahlt" führt zum Beleg (Regel 3).
 */
export function ZahlungDialog(props: {
  api: KassenApi;
  kassenId: string;
  zahlung: Zahlung;
  onFertig: (z: Zahlung) => void;
  titel?: string;
}) {
  const { api, kassenId, zahlung, onFertig } = props;
  /**
   * Zahlungsdialog V2 (Schalter, Vorgabe AUS) – beim Öffnen gelesen, nur andere Darstellung.
   * Nur für die Kartenzahlung des Verkaufs (ohne `titel`); Rückerstattungen (Belege/RU-0,
   * Verkauf) zeigen immer den bisherigen Dialog.
   */
  const [v2] = useState(() => props.titel === undefined && zahlungsdialogV2Aktiv(kassenId));
  const [stand, setStand] = useState<Zahlung>(zahlung);
  const [meldung, setMeldung] = useState<string | null>(null);
  /** Stand gerade nicht abrufbar (Anmeldung/Server-Sperre) – die Zahlung bleibt offen. */
  const [unterbrechung, setUnterbrechung] = useState<string | null>(null);
  const [stornoLaeuft, setStornoLaeuft] = useState(false);
  const fertig = useRef(onFertig);
  fertig.current = onFertig;
  /**
   * ZD-V2-F4: Fokusfalle des bisherigen Dialogs – Startfokus auf dem Dialog selbst (nicht auf
   * „Zahlung abbrechen“), Tab erreicht die Reiter dahinter nicht.
   */
  const dialog = useRef<HTMLDivElement | null>(null);
  /**
   * POS V2 Phase 2, D4/D11: Rückfrage vor dem Zahlungsabbruch (nur Kartenzahlung des Verkaufs,
   * Rückerstattungen mit `titel` wie bisher). Solange sie offen ist, führt sie den Fokus.
   */
  const [abbruchFrage, setAbbruchFrage] = useState(false);
  useFokusfalleAmFenster(dialog, !v2 && !abbruchFrage);

  useEffect(() => {
    const abbruch = new AbortController();
    api
      .warteAufZahlung(kassenId, zahlung.zahlungsreferenz, {
        signal: abbruch.signal,
        beiStand: setStand,
        beiUnterbrechung: setUnterbrechung,
      })
      .then((z) => {
        if (!abbruch.signal.aborted) {
          fertig.current(z);
        }
      })
      .catch((e: unknown) => {
        if (!abbruch.signal.aborted) {
          setMeldung(e instanceof Error ? e.message : String(e));
        }
      });
    return () => {
      abbruch.abort();
    };
  }, [api, kassenId, zahlung.zahlungsreferenz]);

  async function stornieren(): Promise<void> {
    setStornoLaeuft(true);
    try {
      const z = await api.storniereZahlung(kassenId, zahlung.zahlungsreferenz);
      setStand(z);
      setMeldung(
        z.status === 'storniert'
          ? 'Zahlung abgebrochen'
          : 'Abbruch angefordert – das Terminal beendet den Vorgang',
      );
      if (z.status !== 'ausstehend') {
        fertig.current(z);
      }
    } catch (e: unknown) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setStornoLaeuft(false);
    }
  }

  /** Manuelle Klärung am Terminal (ZVT): Terminalbeleg/-journal geprüft, Ergebnis vermerken. */
  async function klaeren(ergebnis: 'bezahlt' | 'nicht_bezahlt'): Promise<void> {
    const frage =
      ergebnis === 'bezahlt'
        ? 'Zeigt der Terminalbeleg bzw. das Terminaljournal diese Zahlung als ERFOLGREICH über genau diesen Betrag? Dann die Belegnummer des Terminalbelegs eingeben:'
        : 'Ist am Terminal KEINE Zahlung gebucht (abgelehnt, abgebrochen oder dort storniert)? Dann mit Namen bestätigen:';
    let terminalBelegnummer: number | undefined;
    if (ergebnis === 'bezahlt') {
      const nr = window.prompt(frage);
      if (nr === null) {
        return;
      }
      terminalBelegnummer = Number(nr.trim());
      if (!Number.isInteger(terminalBelegnummer) || terminalBelegnummer < 0) {
        setMeldung('Terminal-Belegnummer muss eine Zahl sein');
        return;
      }
    }
    const wer = window.prompt(
      ergebnis === 'bezahlt' ? 'Wer hat am Terminal geprüft? (Name)' : frage,
    );
    if (wer === null || wer.trim() === '') {
      return;
    }
    setStornoLaeuft(true);
    try {
      const z = await api.klaereZahlung(kassenId, zahlung.zahlungsreferenz, {
        ergebnis,
        bestaetigtVon: wer.trim(),
        ...(terminalBelegnummer === undefined ? {} : { terminalBelegnummer }),
      });
      setStand(z);
      if (z.status !== 'ausstehend') {
        fertig.current(z);
      }
    } catch (e: unknown) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setStornoLaeuft(false);
    }
  }

  const terminalText =
    typeof stand.details.zwischenstatus === 'string' ? stand.details.zwischenstatus : null;
  const k = stand.details.klaerungNoetig;
  const klaerungsgrund =
    typeof k === 'object' && k !== null && typeof (k as { grund?: unknown }).grund === 'string'
      ? (k as { grund: string }).grund
      : null;
  const uebernommen =
    typeof stand.details.uebernommenAm === 'string' ? Date.parse(stand.details.uebernommenAm) : NaN;
  // Klärbar: klärungsbedürftig gemeldet oder 10 Minuten nach der Übernahme ohne Ergebnis.
  const klaerbar =
    stand.anbieter === 'zvt' &&
    stand.status === 'ausstehend' &&
    (klaerungsgrund !== null ||
      (!Number.isNaN(uebernommen) && Date.now() - uebernommen >= 600_000));

  if (v2) {
    // Gleicher Stand, gleiche Bedingungen und Aufrufe wie unten – nur das V2-Layout
    return (
      <KartenDialogV2
        stand={stand}
        terminalText={terminalText}
        klaerungsgrund={klaerungsgrund}
        klaerbar={klaerbar}
        meldung={meldung}
        unterbrechung={unterbrechung}
        stornoLaeuft={stornoLaeuft}
        onStornieren={() => {
          void stornieren();
        }}
        onKlaeren={(ergebnis) => {
          void klaeren(ergebnis);
        }}
        onSchliessen={() => {
          // Nicht als storniert werten: die Zahlung kann am Terminal noch gelingen.
          fertig.current(stand);
        }}
      />
    );
  }

  // Bedingungen der Tasten – gemeinsam für D4 (Verkauf) und die bisherige Darstellung (Rückerstattung)
  const abbrechbar = !stornoLaeuft && stand.status === 'ausstehend';
  const schliessbar = (meldung !== null || unterbrechung !== null) && stand.status === 'ausstehend';
  const terminalName = stand.anbieter === 'zvt' ? 'Terminal im Laden' : stand.anbieter;
  const warnungen = (
    <>
      {klaerungsgrund !== null && (
        <p className="warnung">
          Ergebnis unklar: {klaerungsgrund}. Die Karte kann belastet sein – am Terminal prüfen und
          dort stornieren, nicht erneut kassieren.
        </p>
      )}
      {meldung !== null && <p className="warnung">{meldung}</p>}
      {unterbrechung !== null && stand.status === 'ausstehend' && (
        <p className="warnung" role="status">
          Zahlungsstand derzeit nicht abrufbar: {unterbrechung} Die Zahlung bleibt offen und wird
          nicht neu gestartet – der Stand wird automatisch wieder abgefragt.
        </p>
      )}
    </>
  );
  const klaerTasten = (
    <>
      {klaerbar && klaerungsgrund === null && (
        <button
          className="taste zweit"
          disabled={stornoLaeuft}
          title="Terminalbeleg zeigt eine erfolgreiche Zahlung über diesen Betrag"
          onClick={() => {
            void klaeren('bezahlt');
          }}
        >
          Terminal: bezahlt
        </button>
      )}
      {klaerbar && (
        <button
          className="taste zweit"
          disabled={stornoLaeuft}
          title="Am Terminal keine Zahlung gebucht bzw. dort storniert"
          onClick={() => {
            void klaeren('nicht_bezahlt');
          }}
        >
          Terminal: nicht bezahlt
        </button>
      )}
      {schliessbar && (
        <button
          className="taste zweit"
          title="Das Ergebnis ist unbekannt – die Kasse merkt sich die Zahlung und prüft sie später"
          onClick={() => {
            // Nicht als storniert werten: die Zahlung kann am Terminal noch gelingen.
            fertig.current(stand);
          }}
        >
          Dialog schließen
        </button>
      )}
    </>
  );

  if (props.titel === undefined) {
    // POS V2 Phase 2, Rahmen D4 `16:1975`: Kartenzahlung läuft – gleiche Werte und Aufrufe
    return (
      <div
        ref={dialog}
        className="zahlung-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="karte-titel"
        tabIndex={-1}
        style={{ outline: 'none' }}
      >
        <div className="karte dlg karten-dialog">
          <div className="dlg-kopf">
            <h2 id="karte-titel">Kartenzahlung</h2>
            <span className="dlg-meta">
              {terminalName} · {stand.zahlungsreferenz}
            </span>
          </div>
          <div className="dlg-betragszeile">
            <span>Am Terminal</span>
            <span className="betrag">{formatiereEuro(stand.betragCent)} €</span>
          </div>
          {stand.trinkgeldCent !== undefined && stand.trinkgeldCent > 0 && (
            <p className="hinweis">
              davon Trinkgeld {formatiereEuro(stand.trinkgeldCent)} € (kein Umsatz)
            </p>
          )}
          <div className="dlg-info" role="status">
            <p>
              Stand: <strong>{stand.status}</strong>
              {terminalText !== null && ` – ${terminalText}`}
            </p>
            {stand.status === 'ausstehend' && (
              <p className="hinweis">
                Der Beleg entsteht erst, wenn das Terminal „bezahlt“ meldet.
              </p>
            )}
          </div>
          {warnungen}
          {stand.status === 'ausstehend' && (
            <p className="dlg-sperre hinweis">
              Kasse gesperrt bis zum Abschluss: Kacheln, Suche, Scanner und Funktionstasten
              reagieren nicht.
            </p>
          )}
          <div className="dlg-aktionen">
            <button
              className="taste gefahr dlg-breit"
              disabled={!abbrechbar}
              onClick={() => {
                setAbbruchFrage(true);
              }}
            >
              Zahlung abbrechen
            </button>
            {klaerTasten}
          </div>
        </div>
        {abbruchFrage && (
          <Rueckfrage
            titel="Kartenzahlung abbrechen?"
            icon="karte"
            text={`Der Abbruch wird beim Terminal angefordert (${formatiereEuro(stand.betragCent)} €). Bis das Terminal antwortet, bleibt die Kasse gesperrt. Meldet es „bezahlt“, entsteht der Beleg trotzdem; meldet es „abgebrochen“, entsteht kein Beleg.`}
            zurueck="Weiter warten"
            bestaetigen="Zahlung abbrechen"
            onZurueck={() => {
              setAbbruchFrage(false);
            }}
            onBestaetigen={() => {
              setAbbruchFrage(false);
              // Stand kann sich während der Rückfrage geändert haben – nur noch offen abbrechen
              if (abbrechbar) {
                void stornieren();
              }
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={dialog}
      className="zahlung-dialog"
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      style={{ outline: 'none' }}
    >
      <div className="karte">
        <h2>{props.titel}</h2>
        <div className="betrag">{formatiereEuro(stand.betragCent)} €</div>
        {stand.trinkgeldCent !== undefined && stand.trinkgeldCent > 0 && (
          <p className="hinweis">
            davon Trinkgeld {formatiereEuro(stand.trinkgeldCent)} € (kein Umsatz)
          </p>
        )}
        <p className="hinweis">
          {stand.anbieter === 'zvt' ? 'Terminal im Laden' : stand.anbieter} ·{' '}
          {stand.zahlungsreferenz}
        </p>
        <p>
          Stand: <strong>{stand.status}</strong>
          {terminalText !== null && ` – ${terminalText}`}
        </p>
        {warnungen}
        <div className="zeile" style={{ justifyContent: 'center' }}>
          <button
            className="taste gefahr"
            disabled={!abbrechbar}
            onClick={() => {
              void stornieren();
            }}
          >
            Zahlung abbrechen
          </button>
          {klaerTasten}
        </div>
      </div>
    </div>
  );
}
